import { useEffect, useState } from 'react';
import { useXClone } from '@/state/xclone';
import { useSettings } from '@/state/settings';
import { listCompanies } from '@/content/tickers';
import { priceAt } from '@/engine/prices';
import { priceFmt, pct } from '@/lib/format';
import { formatRelativeTime } from '@/lib/time';
import { cn } from '@/lib/cn';

interface Channel {
  id: string;
  callSign: string;
  netLabel: string;
  spotlightLabel: string;
  onAirLabel: string;
  trendLabel: string;
  anchors: { name: string; show: string }[];
  quips: string[];
  spin: (positive: boolean) => string;
}

const CHANNELS: Channel[] = [
  {
    id: 'cnbn',
    callSign: 'CNBN',
    netLabel: 'CNBN — Live',
    spotlightLabel: 'market spotlight',
    onAirLabel: 'on air now',
    trendLabel: 'trending — what people are saying',
    anchors: [
      { name: 'Tia Banzai', show: 'PRE-MARKET PULSE' },
      { name: 'Brock Hannigan', show: 'WALL STREET WAR ROOM' },
      { name: 'Jiminy Vance', show: 'CLOSING BELL CHAOS' },
      { name: 'Marigold Sterling', show: 'AFTER-HOURS AGONY' },
      { name: 'Cassidy Lehrer-Pham', show: 'TECH CHECK' },
      { name: 'Bryce Pundit', show: 'POL TICKER PROBE' },
      { name: 'Jim Cranier', show: 'MAD MARGIN' },
    ],
    quips: [
      "you really have to ask yourself: is this a market, or are we already past it.",
      "the bid is real. the offer is also real. the difference between them: vibes.",
      "in a hawkish tape, you want to be in defensives. in a dovish tape, also defensives.",
      "i\'ve seen this exact setup in 2007. and 2018. and 2022. it ended differently each time.",
      "the chart is fine. the chart is always fine. that\'s why we look at the chart.",
      "we\'re going to get a guest in 14 minutes. she will disagree with everything i just said. she will also be wrong.",
      "if you\'re not nervous right now, you\'re not paying attention. if you ARE nervous, you\'re also not paying attention.",
    ],
    spin: (pos) => pos
      ? "Up. That's the chart, that's all the chart will tell you."
      : "Down. That's the entire technical case.",
  },
  {
    id: 'maga-tv',
    callSign: 'TPTV',
    netLabel: 'TRUE PATRIOT TV — 24/7',
    spotlightLabel: 'what they don\'t want you to see',
    onAirLabel: 'live from the bunker',
    trendLabel: 'real americans are saying',
    anchors: [
      { name: 'Hannity Lemonade', show: 'PATRIOTS & PORTFOLIOS' },
      { name: 'Tucker Lite', show: 'HOT TAKE BUFFET' },
      { name: 'Diamond Sledgehammer', show: 'AMERICA FIRST FINANCE' },
      { name: 'Major Buck Steele (ret.)', show: 'STRENGTH HOUR' },
      { name: 'Pastor Jed Calhoun', show: 'THE DAILY WORD ON MARKETS' },
    ],
    quips: [
      "the deep state doesn't want you in this stock. that's why you should be in this stock.",
      "Wall Street hates one weird trick. the trick is just buying things and not selling them.",
      "real americans buy stocks. fake americans, somehow, do not.",
      "the founding fathers, if alive today, would be long this exact ticker.",
      "they're not telling you about this rally. we are. you're welcome.",
      "remember: the only patriotic position is long. shorting is for the European Union.",
    ],
    spin: (pos) => pos
      ? "MASSIVE win for the patriot economy. unreported by mainstream sources, obviously."
      : "they did this to your portfolio on purpose. ask yourself who benefits. spoiler: not you.",
  },
  {
    id: 'doom-prep',
    callSign: 'BNK7',
    netLabel: 'BUNKER NETWORK 7',
    spotlightLabel: 'asset to liquidate before collapse',
    onAirLabel: 'broadcasting from undisclosed location',
    trendLabel: 'survivors are saying',
    anchors: [
      { name: 'Doctor Cassandra Vega', show: 'COLLAPSE WATCH' },
      { name: 'Sergeant Wade Pratt', show: 'GOLD, GUNS & GROCERIES' },
      { name: 'Mother Helen Wickliffe', show: 'THE END IS NIGH HOUR' },
      { name: 'Rick Tarp', show: 'FREEZE-DRIED FUTURES' },
    ],
    quips: [
      "i give the dollar 6 to 11 weeks. i\'ve been saying that for 14 years. eventually i\'m right.",
      "if you can\'t physically eat your portfolio, your portfolio is fake.",
      "the elites are buying canned beans. they aren\'t telling you that.",
      "rates, debt, demographics, supply chains, the moon — pick any reason. we are unwell.",
      "you laugh. you laughed in 1999. you laughed in 2008. you stopped laughing eventually.",
    ],
    spin: (pos) => pos
      ? "every green tape in history has been the top. every single one. statistically."
      : "and so it begins. exactly as forecast. exactly. as. forecast.",
  },
  {
    id: 'late-night',
    callSign: 'NITELY',
    netLabel: 'NITELY — late night',
    spotlightLabel: 'tonight\'s monologue ticker',
    onAirLabel: 'tonight on the show',
    trendLabel: 'audience reactions',
    anchors: [
      { name: 'Jamie Klein', show: 'JAMIE LIVE' },
      { name: 'Devon Marsh', show: 'THE LATE PUNCH' },
      { name: 'Rinaldo Pesto', show: 'THE PESTO HOUR' },
    ],
    quips: [
      "and if you held this stock through that — first of all, why. second of all, are you okay.",
      "we have a clip. of course we have a clip. roll the clip. wait, no, roll the OTHER clip.",
      "today the market did a thing. it does that. that\'s its hobby.",
      "we asked traders for comment. one said \"please.\" another said \"please. stop.\"",
      "this story has everything. cash. crime. a guy named steve. you cannot make this up. we did not.",
    ],
    spin: (pos) => pos
      ? "stock went up. people who own it: thrilled. people who don\'t: weirdly mad."
      : "stock went down. somebody is, right now, telling their wife. we wish them well.",
  },
];

export function TV() {
  const [now, setNow] = useState(() => Date.now());
  const [channelIdx, setChannelIdx] = useState(0);
  const allPosts = useXClone((s) => s.posts);
  const posts = allPosts.slice(0, 6);
  const playerHandle = useSettings((s) => s.playerHandle);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const channel = CHANNELS[channelIdx]!;
  const anchor = channel.anchors[Math.floor(now / 60_000) % channel.anchors.length]!;
  const quip = channel.quips[Math.floor(now / 30_000) % channel.quips.length]!;
  const featuredCompany = listCompanies()[Math.floor(now / 30_000) % listCompanies().length]!;
  const featuredPrice = priceAt(featuredCompany, now);
  const featuredRef = priceAt(featuredCompany, now - 24 * 60 * 60 * 1000);
  const featuredChange = featuredRef > 0 ? featuredPrice / featuredRef - 1 : 0;
  const positive = featuredChange >= 0;

  return (
    <div className="flex h-full flex-col bg-[var(--bg-deep)]">
      <header className="flex items-center justify-between border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] px-4 py-3">
        <span className="text-mono-md font-bold text-[var(--text-primary)]">{channel.netLabel}</span>
        <span className="flex items-center gap-1 text-mono-xs text-[var(--negative)]">
          <span className="inline-block h-2 w-2 rounded-full bg-[var(--critical)] animate-pulse" />
          LIVE
        </span>
      </header>

      {/* Channel selector */}
      <div className="flex items-center gap-1 overflow-x-auto border-b border-[var(--border-subtle)] bg-[var(--bg-deep)] px-2 py-1">
        {CHANNELS.map((c, i) => (
          <button
            key={c.id}
            onClick={() => setChannelIdx(i)}
            className={cn(
              'shrink-0 rounded px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition-colors',
              i === channelIdx
                ? 'bg-[var(--accent-primary)] text-black'
                : 'text-[var(--text-muted)] hover:bg-[var(--bg-elevated)] hover:text-[var(--text-secondary)]',
            )}
          >
            {c.callSign}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Featured ticker block */}
        <section className="border-b border-[var(--border-subtle)] bg-[var(--bg-surface)] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{channel.spotlightLabel}</div>
          <div className="mt-1 flex items-baseline gap-3">
            <span className="text-mono-lg font-bold text-[var(--text-primary)]">{featuredCompany.id}</span>
            <span className="text-mono-md tabular-nums text-[var(--text-primary)]">{priceFmt(featuredPrice)}</span>
            <span className={cn('text-mono-sm tabular-nums', positive ? 'text-[var(--positive)]' : 'text-[var(--negative)]')}>
              {pct(featuredChange)}
            </span>
          </div>
          <p className="mt-2 text-mono-xs text-[var(--text-secondary)]">{featuredCompany.lore}</p>
        </section>

        {/* Anchor block */}
        <section className="border-b border-[var(--border-subtle)] bg-[var(--bg-deep)] p-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{channel.onAirLabel}</div>
          <div className="mt-1 font-news text-xl text-[var(--text-primary)]">{anchor.show}</div>
          <div className="text-mono-xs text-[var(--text-secondary)]">with {anchor.name}</div>
          <p className="mt-2 text-mono-sm text-[var(--text-primary)] italic">
            "{quip} {channel.spin(positive)} We'll have more after the break."
          </p>
        </section>

        {/* Trending from Y feed (cross-surface) */}
        <section className="p-4">
          <div className="mb-2 text-[10px] uppercase tracking-widest text-[var(--text-muted)]">{channel.trendLabel}</div>
          <div className="flex flex-col gap-2">
            {posts.length === 0 && (
              <p className="text-mono-xs italic text-[var(--text-muted)]">no chatter. yet.</p>
            )}
            {posts.map((p) => (
              <div key={p.id} className="border-l-2 border-[var(--border-strong)] pl-3 text-mono-xs">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-bold text-[var(--text-primary)]">@{p.authorIsSelf ? playerHandle : p.authorHandle}</span>
                  <span className="text-[var(--text-muted)]">{formatRelativeTime(p.timestamp)}</span>
                </div>
                <p className="text-[var(--text-secondary)]">{p.text}</p>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
